import { Button, Campaign, defaultImage, LogService, Prizes, Ranking, Tag, ToastHelper } from '@hub/shared'
import { SqModal } from '@squidit/react-css'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './modal-checkout-prize.component.scoped.scss'
import './modal-checkout-prize.component.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  prize: Prizes
  userRanking: Ranking
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onCloseChange: () => void
  onConfirmCheckout: () => void
  campaign: Campaign
}

export default ({
  className = '',
  style = {},
  prize,
  userRanking,
  onOpenChange,
  open,
  onCloseChange,
  onConfirmCheckout,
  campaign,
}: Props) => {
  const [t] = useTranslation('engage')
  const [loading, setLoading] = useState(false)
  const logService = useMemo(() => new LogService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  logService.init('Modal Checkout Prize')

  const doCheckout = useCallback(async () => {
    if (!loading) {
      setLoading(true)
      try {
        toast.success(t('prizesModal.success'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
        onConfirmCheckout()
      } catch (error) {
        logService.error({
          message: 'Failed to do checkout',
          error,
        })
        toast.error(t('prizesModal.error'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLoading, onOpenChange, prize, userRanking, campaign, t, toast, logService, onConfirmCheckout])

  return (
    <SqModal
      forceMobileNoMargin
      className={`modal-checkouts-prizes ${className}`}
      open={open}
      onOpenChange={(open: boolean) => onOpenChange(open)}
      onCloseChange={() => onCloseChange()}
      style={style}
      header={<h6 className="m-0">{t('prizesModal.title')}</h6>}
      footer={
        <div className="wrapper-footer-modal-checkouts-prizes">
          <Button
            disabled={loading}
            onClick={() => onCloseChange()}
            inverted
            color="transparent"
            borderColor="var(--gray)"
            textColor="var(--gray)"
            className="cancel-button-modal-checkouts"
          >
            {t('prizesModal.cancel')}
          </Button>
          <Button loading={loading} onClick={() => doCheckout()}>
            {t('prizesModal.confirm')}
          </Button>
        </div>
      }
    >
      <h6 className="mt-3">{t('prizesModal.label')}</h6>
      <div className="prize">
        <header>
          <figure>
            <img
              src={prize?.image?.thumbnail}
              className="img-fluid"
              title={prize?.value}
              alt={prize?.value}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = defaultImage
              }}
            />
          </figure>
          <div className="texts">
            <h6>{prize?.value}</h6>
            <div className="tags">
              <Tag labels={[`${prize?.points} ${t('points')}`]} separator />
            </div>
          </div>
        </header>
        <p dangerouslySetInnerHTML={{ __html: t('prizesModal.balance', { points: userRanking?.totalAvailable - prize?.points }) }} />
      </div>
    </SqModal>
  )
}
